import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/use-auth';
import { MetaTags } from '../../components/seo/MetaTags';
import { motion } from 'framer-motion';
import { CreditStatus } from '../../components/credits/CreditStatus';
import { IdentificationPanel } from '../Dashboard/components/IdentificationPanel';
import { HistoryPanel } from '../Dashboard/components/HistoryPanel';
import { CreditPurchaseModal } from '../../components/credits/CreditPurchaseModal';
import { LoadingSpinner } from '../../components/loading/LoadingSpinner';
import { EmailVerificationBanner } from '../Dashboard/components/EmailVerificationBanner';
import {
  deductIdentificationCredit,
  getUserCredits,
  purchaseCredits
} from '../../lib/credits';
import { useToast } from '../../hooks/use-toast';
import { getUserHistory, saveIdentification } from '../../lib/firestore';
import { identifyPlant } from '../../lib/google-ai'; // Add at the top with other imports
import { Analytics } from '../../lib/firebase-analytics';

// Helper function to extract plant name from identification result
// Helper function to extract plant name from identification result
function extractPlantName(result) {
  if (!result) return 'Unknown Plant';

  // Look for "Common and Scientific Name" section
  const commonNameMatch = result.match(/Common and Scientific Name:([^(]+)/i);
  if (commonNameMatch && commonNameMatch[1]) {
    return commonNameMatch[1].trim();
  }

  // Fallback to first line if no match found
  return result.split('\n')[0].trim();

  // return 'Unknown Plant';
}

export function Identifier() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState('upload');
  const [imageData, setImageData] = useState(null);
  const [credits, setCredits] = useState({ regular: 0, premium: 0, total: 0 });
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      if (user) {
        try {
          setIsLoadingCredits(true);
          setIsHistoryLoading(true);
          const [userCredits, plantHistory] = await Promise.all([
            getUserCredits(user.uid),
            getUserHistory(user.uid)
          ]);
          setCredits(userCredits);
          setHistory(plantHistory);
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to fetch data',
            variant: 'destructive'
          });
        } finally {
          setIsLoadingCredits(false);
          setIsHistoryLoading(false);
        }
      }
    }

    fetchData();
  }, [user, toast]);

  const handleImage = async data => {
    if (!user) {
      Analytics.logFeatureInteraction(
        'disease_detection',
        'insufficient_credits'
      );
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to identify plants.',
        variant: 'destructive'
      });
      return;
    }

    // Check if user has enough credits before making the API call
    if (credits.total < 1) {
      toast({
        title: 'Insufficient Credits',
        description:
          'You need 1 credits to Identify Plants. Please purchase more credits to continue.',
        variant: 'destructive'
      });
      setIsPurchaseModalOpen(true);
      return;
    }

    // Track identification attempt
    Analytics.logFeatureInteraction('identifier', 'start_identification');

    try {
      setImageData(data);
      setIsIdentifying(true);
      setError(null);
      setResult(null);

      // First deduct credits
      const updatedCredits = await deductIdentificationCredit(user.uid);
      setCredits(updatedCredits);

      // Track credit usage
      Analytics.logCreditUsage(1, 'plant_identification');

      // Then make the API call
      const plantInfo = await identifyPlant(data);

      if (!plantInfo || plantInfo.trim() === '') {
        throw new Error(
          'Unable to identify plant. Please try again with a clearer image.'
        );
      }

      // Track successful identification
      Analytics.logPlantIdentification(true, {
        plant_name: extractPlantName(plantInfo)
      });

      setResult(plantInfo);

      // Extract plant name and save identification
      const plantName = extractPlantName(plantInfo);
      await saveIdentification(user.uid, {
        plantInfo,
        plantName,
        imageUrl: data,
        timestamp: new Date().toISOString()
      });

      // Update history
      const updatedHistory = await getUserHistory(user.uid);
      setHistory(updatedHistory);
    } catch (error) {
      setError(error.message);
      // Track identification error
      Analytics.logIdentificationError(error);
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to identify plant. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsIdentifying(false);
    }
  };

  const handlePurchaseCredits = async amount => {
    try {
      await purchaseCredits(user.uid, amount);
      const newCredits = await getUserCredits(user.uid);
      setCredits(newCredits);
      toast({
        title: 'Success',
        description: `Successfully purchased ${amount} credits!`,
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to purchase credits',
        variant: 'destructive'
      });
    }
  };

  const handlePlantClick = plant => {
    setSelectedPlant(plant);
    setIsModalOpen(true);
  };

  if (!user?.emailVerified) {
    return <EmailVerificationBanner />;
  }

  return (
    <>
      <MetaTags
        title="Plant Identifier"
        description="Identify any plant using our AI-powered tool."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 to-transparent pt-24 px-4 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto space-y-8"
        >
          {isLoadingCredits ? (
            <div className="bg-white rounded-lg p-6 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <CreditStatus
              credits={credits.total}
              onBuyCredits={() => setIsPurchaseModalOpen(true)}
            />
          )}

          {!isLoadingCredits && (
            <IdentificationPanel
              mode={mode}
              setMode={setMode}
              imageData={imageData}
              isIdentifying={isIdentifying}
              error={error}
              result={result}
              onImageCapture={handleImage}
              disabled={credits.total <= 0}
            />
          )}

          <HistoryPanel
            history={history}
            isLoading={isHistoryLoading}
            selectedPlant={selectedPlant}
            isModalOpen={isModalOpen}
            onPlantClick={handlePlantClick}
            onModalClose={() => setIsModalOpen(false)}
          />
        </motion.div>

        <CreditPurchaseModal
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          onPurchase={handlePurchaseCredits}
        />
      </div>
    </>
  );
}
